import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../services/apiService';
import { Cliente } from '../../data/Cliente';
import { ListaPaginada } from '../../data/ListaPaginada';
import Pagination from '../../componets/Pagination';
import ConfirmationModal from '../../componets/ConfirmationModal';

function Clientes() {
  const [data, setData] = useState<ListaPaginada<Cliente> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [clienteToDelete, setClienteToDelete] = useState<number | null>(null);
  const itemsPerPage = 10; // Itens por página

  useEffect(() => {
    const fetchClientes = async () => {
      setLoading(true);
      try {
        const endpoint = `/usuarios/Clientes?pagina=${currentPage}&tamanhoPagina=${itemsPerPage}`;
        const responseData = await apiRequest<ListaPaginada<Cliente>>(endpoint, 'GET', true);
        setData(responseData);
      } catch (err) {
        setError((err as Error).message || 'Erro ao carregar os Clientes.');
      } finally {
        setLoading(false);
      }
    };
    fetchClientes();
  }, [currentPage]);

  const totalRecords = data?.totalItens ?? 0;
  const totalPages = data?.totalPaginas ?? 0;
  const currentItems = data?.itens ?? [];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Abre o modal de confirmação
  const handleDeleteClick = (id: number) => {
    setClienteToDelete(id);
    setShowDeleteModal(true);
  };

  // Executa a exclusão após a confirmação
  const confirmDelete = async () => {
    if (!clienteToDelete) return;

    setShowDeleteModal(false);
    setLoading(true); 
    setError(null);

    try {
      // Assumindo um endpoint POST para exclusão segura, similar ao de corretores
      await apiRequest(`usuarios/deletecliente/${clienteToDelete}`, 'POST', true);

      // Recarrega os dados da página atual para refletir a exclusão
      const fetchClientes = async () => {
        const endpoint = `/usuarios/Clientes?pagina=${currentPage}&tamanhoPagina=${itemsPerPage}`;
        const responseData = await apiRequest<ListaPaginada<Cliente>>(endpoint, 'GET', true);
        setData(responseData);
      };
      await fetchClientes();
    } catch (err) {
      setError((err as Error).message || 'Erro ao excluir o cliente.');
    } finally {
      setLoading(false);
      setClienteToDelete(null);
    }
  };

  if (loading) {
    return <p>Carregando Clientes...</p>;
  }

  if (error) {
    return <p>Erro: {error}</p>;
  }

  return (
    <div className="row">
      <div className="card">
        <div className="card-body">
          <div className="mb-3">
            <h1 className="h3 d-inline align-middle">Clientes</h1>
          </div>
          <div className="mb-3 text-end">
            <Link to="/Clientes/novo" className="btn btn-primary"> Novo <i className="bi bi-plus"></i></Link>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th className="col-2">#</th>
                <th className="col">Nome</th>
                <th className="col">CPF</th>
                <th className="col-3">Ações</th>
              </tr>
            </thead>
            <tbody>
            {currentItems.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center">Nenhum cliente cadastrado.</td>
              </tr>
            ) : (currentItems.map(cliente => (
              <tr key={cliente.id}>
                <td>{cliente.id}</td>
                <td>{cliente.nome}</td>
                <td>{cliente.cpf}</td>
                <td>
                  <div className="d-flex gap-4">
                    <Link to={`/Clientes/editar/${cliente.id}`} className="btn btn-outline-primary"><i className="align-middle bi bi-pencil-square"></i> Editar</Link>
                    <button onClick={() => handleDeleteClick(cliente.id!)} className="btn btn-outline-danger">
                      <i className="align-middle bi bi-trash"></i> Excluir
                    </button>
                  </div>
                </td>
              </tr>
              ))
            )}
            </tbody>
          </table>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalRecords={totalRecords}
            onPageChange={handlePageChange}
          />
          <ConfirmationModal
            show={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={confirmDelete}
            title="Confirmar Exclusão"
            body="Tem certeza que deseja excluir este cliente? Esta ação não pode ser desfeita."
            confirmText="Excluir"
            confirmButtonVariant="danger"
          />
        </div>
      </div>
    </div>
  );
}

export default Clientes;